import {
  FAMILY_CLAIMS,
  FAMILY_SECTION,
  type NavFamily,
  type SectionId,
} from '@/components/layout/nav-items'

/**
 * Where the member is, in the two vocabularies the chrome speaks.
 *
 * One resolution, two readers: `resolveFamily` picks the sidebar section that
 * lights (nav-groups.ts), and `resolveActiveSection` — derived from it, never
 * computed apart — picks the bottom-bar slot. A stock page is the `stock`
 * family and the `market` section; because the second is read off the first,
 * the bar and the rail cannot disagree about where the member is.
 *
 * Both are PURE functions of the pathname with the router nowhere in sight,
 * because "which section lights at /market/depth" is a question about strings
 * and data, and a question about strings should not need a rendered tree.
 */

/** True when `pathname` IS `prefix` or continues it at a segment boundary. */
export const withinPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

/**
 * The family a path belongs to, or `null` for a route no family claims.
 *
 * LONGEST segment-aware claim wins — the rule that carves Signal Zone's
 * `/market/signals` pages out of `/market` into a section of their own (and
 * `/market/issues` the same way), as it once carved Market update into the
 * old News & reports group (reversed by the owner, 2026-09-24); a future
 * exception needs only a longer, more specific entry there.
 * Segment-aware, not a bare `startsWith`: `/marketing` is not inside Market,
 * and `/` claims only itself — `withinPrefix(p, '/')` would need `p` to start
 * with `//`.
 */
export function resolveFamily(pathname: string): NavFamily | null {
  let best: { length: number; family: NavFamily } | null = null
  for (const [prefix, family] of FAMILY_CLAIMS) {
    if (!withinPrefix(pathname, prefix)) continue
    if (best === null || prefix.length > best.length) best = { length: prefix.length, family }
  }
  return best?.family ?? null
}

/**
 * The bottom-bar section a path belongs to, or `null` for "light nothing" —
 * Watchlist, Alerts, Account and every unclaimed route.
 *
 * Home is `/` and `/notifications`; Market is all of `/market/*`,
 * `/newsletters/*` and every `/stocks/*` page; Learn is `/courses/*` and
 * `/learn/*` — the mapping lives in FAMILY_SECTION (nav-items.ts).
 */
export function resolveActiveSection(pathname: string): SectionId | null {
  const family = resolveFamily(pathname)
  return family === null ? null : FAMILY_SECTION[family]
}
